import React, { Component } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import InputField from './inputs/inputField';
import FileChooserPreview from './inputs/fileChooserPreview';
import FilesUploaderModal from './files/filesUploaderModal';
import ToggleField from './inputs/toggleField';
import SelectField from './inputs/selectField';

class BadgeEditor extends Component {
	constructor(props) {
		super(props);

		const defaultConfig = {
			badgeWidth: 332, // pixels
			badgeHeight: 529, // pixels
			fieldsConfig: [],
			enableQrCode: true,
			qrCode: { x: 15, y: 415, size: 100 },
			backgroundImage: 'https://assets-simpleevents.s3.amazonaws.com/betaorg/SE_66c8ca9f1913e99e9baebe93/assets/BADGETEMPLATE%20in%20MM.png',
			includeBackground: true
		};
		const initialConfig = this.props.initialConfig ? { ...defaultConfig, ...this.props.initialConfig } : defaultConfig;
		this.state = {
			baseFields: [
				{ value: 'firstName', label: 'First Name' },
				{ value: 'lastName', label: 'Last Name' },
				{ value: 'fullName', label: 'Full Name' },
				{ value: 'email', label: 'Email' },
				{ value: 'company', label: 'Company' }
			],
			config: JSON.parse(JSON.stringify(initialConfig)),
			baseConfiguration: JSON.parse(JSON.stringify(initialConfig)),
			scaleX: 400 / 1040,
			scaleY: 637 / 1654,
			draggedElement: null,
			isResizing: false,
			resizeHandle: null,
			offsetX: 0,
			offsetY: 0,
			originalRect: null,
			originalConfig: null,
			dragUpdate: 0,
			badgePanelOpen: true,
			editing: false,
			uploadingBackground: false,
			selectedBaseField: null

		};
		this.badgeContainerRef = React.createRef();
		this.fieldRefs = {};
		this.qrElementRef = React.createRef();
		this.backgroundImgRef = React.createRef();
	}

	componentDidMount() {
		this.updateDisplay();
		document.addEventListener('mousemove', this.handleMouseMove);
		document.addEventListener('mouseup', this.handleMouseUp);
	}

	componentWillUnmount() {
		document.removeEventListener('mousemove', this.handleMouseMove);
		document.removeEventListener('mouseup', this.handleMouseUp);
	}

	clamp = (value, min, max) => {
		return Math.max(min, Math.min(max, value));
	};

	updateDisplay = () => {
		const { config } = this.state;

		// Update background
		if (this.badgeContainerRef.current) {
			this.badgeContainerRef.current.style.backgroundImage = config.backgroundImage ? `url(${config.backgroundImage})` : 'none';
			this.badgeContainerRef.current.style.backgroundSize = 'cover';
			this.badgeContainerRef.current.style.backgroundPosition = 'center';
		}

		if (this.backgroundImgRef.current) {
			this.backgroundImgRef.current.src = config.backgroundImage || '';
			this.backgroundImgRef.current.classList.toggle('print-include', config.includeBackground);
		}

		// Canvas dimensions: actual 88mm × 140mm at 96 PPI
		const canvasWidthPx = Math.round((88 / 25.4) * 96);   // 333px - actual 88mm at 96 PPI (rounded to whole pixels)
		const canvasHeightPx = Math.round((140 / 25.4) * 96); // 529px - actual 140mm at 96 PPI (rounded to whole pixels)

		// Scale factors: how many display pixels per real badge pixel
		const scaleX = canvasWidthPx / config.badgeWidth;
		const scaleY = canvasHeightPx / config.badgeHeight;

		// Update dynamic field positions
		config.fieldsConfig.forEach((field, index) => {
			const element = this.fieldRefs[`field-${index}`];
			if (element) {
				element.style.left = (field.x * scaleX) + 'px';
				element.style.top = (field.y * scaleY) + 'px';
				element.style.fontSize = field.fontSize + 'px';
				element.style.fontFamily = field.fontFamily || 'Inter, sans-serif';
			}
		});

		// Update QR code position
		if (this.qrElementRef.current) {
			this.qrElementRef.current.style.left = (config.qrCode.x * scaleX) + 'px';
			this.qrElementRef.current.style.top = (config.qrCode.y * scaleY) + 'px';
			this.qrElementRef.current.style.width = (config.qrCode.size * scaleX) + 'px';
			this.qrElementRef.current.style.height = (config.qrCode.size * scaleY) + 'px';
			this.qrElementRef.current.style.fontFamily = 'Inter, sans-serif';
		}
	};

	handleMouseDown = (e) => {
		const target = e.target;

		if (target.classList.contains('resize-handle')) {
			this.setState({
				isResizing: true,
				resizeHandle: target,
				draggedElement: target.parentElement,
				originalRect: target.parentElement.getBoundingClientRect(),
				originalConfig: JSON.parse(JSON.stringify(this.state.config))
			});
			target.parentElement.style.zIndex = 1000;
		} else if (target.id.startsWith('field-') || target.id === 'qrElement' || (target.parentElement && (target.parentElement.id.startsWith('field-') || target.parentElement.id === 'qrElement'))) {
			const element = (target.id.startsWith('field-') || target.id === 'qrElement') ? target : target.parentElement;
			const rect = element.getBoundingClientRect();
			this.setState({
				draggedElement: element,
				offsetX: e.clientX - rect.left,
				offsetY: e.clientY - rect.top
			});
			element.style.zIndex = 1000;
		}
	};

	handleMouseMove = (e) => {
		const { isResizing, resizeHandle, draggedElement, originalRect, originalConfig, config } = this.state;

		if (isResizing && draggedElement && originalRect && originalConfig) {
			const containerRect = this.badgeContainerRef.current.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;

			const mouseX = e.clientX - containerRect.left;
			const mouseY = e.clientY - containerRect.top;

			let newLeft = originalRect.left - containerRect.left;
			let newTop = originalRect.top - containerRect.top;
			let newWidth = originalRect.width;
			let newHeight = originalRect.height;

			if (resizeHandle.classList.contains('se')) {
				newWidth = mouseX - newLeft;
				newHeight = mouseY - newTop;
			} else if (resizeHandle.classList.contains('sw')) {
				newWidth = (originalRect.right - containerRect.left) - mouseX;
				newHeight = mouseY - newTop;
				newLeft = mouseX;
			} else if (resizeHandle.classList.contains('ne')) {
				newWidth = mouseX - newLeft;
				newHeight = (originalRect.bottom - containerRect.top) - mouseY;
				newTop = mouseY;
			} else if (resizeHandle.classList.contains('nw')) {
				newWidth = (originalRect.right - containerRect.left) - mouseX;
				newHeight = (originalRect.bottom - containerRect.top) - mouseY;
				newLeft = mouseX;
				newTop = mouseY;
			}

			// Clamp dimensions to container boundaries
			newWidth = this.clamp(newWidth, 50, containerWidth);
			newHeight = this.clamp(newHeight, 30, containerHeight);
			newLeft = this.clamp(newLeft, 0, containerWidth - newWidth);
			newTop = this.clamp(newTop, 0, containerHeight - newHeight);

			// Update element style
			draggedElement.style.left = newLeft + 'px';
			draggedElement.style.top = newTop + 'px';
			if (draggedElement.id === 'qrElement') {
				// Keep QR code as a square - use the larger dimension for both width and height
				const squareSize = Math.max(newWidth, newHeight);
				draggedElement.style.width = squareSize + 'px';
				draggedElement.style.height = squareSize + 'px';
				newWidth = squareSize;
				newHeight = squareSize;
			}

			// Calculate dynamic scaling for precise coordinate mapping
			const scaleX = containerWidth / config.badgeWidth;
			const scaleY = containerHeight / config.badgeHeight;

			// Update config with precise real coordinates (maintain float precision)
			const realLeft = newLeft / scaleX;
			const realTop = newTop / scaleY;
			const realWidth = newWidth / scaleX;
			const realHeight = newHeight / scaleY;

			let newConfig = { ...config };

			if (draggedElement.id === 'qrElement') {
				newConfig.qrCode.x = Math.round(this.clamp(realLeft, 0, config.badgeWidth - realWidth));
				newConfig.qrCode.y = Math.round(this.clamp(realTop, 0, config.badgeHeight - realHeight));
				newConfig.qrCode.size = Math.round(Math.max(realWidth, realHeight));
				newConfig.qrCode.size = this.clamp(newConfig.qrCode.size, 5, 50);
			} else if (draggedElement.id.startsWith('field-')) {
				const fieldIndex = parseInt(draggedElement.id.split('-')[1]);
				if (newConfig.fieldsConfig[fieldIndex]) {
					newConfig.fieldsConfig[fieldIndex].x = Math.round(this.clamp(realLeft, 0, config.badgeWidth - 5));
					newConfig.fieldsConfig[fieldIndex].y = Math.round(this.clamp(realTop, 0, config.badgeHeight - 5));
					newConfig.fieldsConfig[fieldIndex].fontSize = Math.round(originalConfig.fieldsConfig[fieldIndex].fontSize * (newHeight / originalRect.height));
					newConfig.fieldsConfig[fieldIndex].fontSize = this.clamp(newConfig.fieldsConfig[fieldIndex].fontSize, 10, 500);
				}
			}

			this.setState({ config: newConfig, dragUpdate: this.state.dragUpdate + 1, editing: true });
		} else if (draggedElement) {
			const containerRect = this.badgeContainerRef.current.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;

			let newLeft = e.clientX - containerRect.left - this.state.offsetX;
			let newTop = e.clientY - containerRect.top - this.state.offsetY;

			// Clamp to container boundaries
			newLeft = this.clamp(newLeft, 0, containerWidth - draggedElement.offsetWidth);
			newTop = this.clamp(newTop, 0, containerHeight - draggedElement.offsetHeight);

			draggedElement.style.left = newLeft + 'px';
			draggedElement.style.top = newTop + 'px';

			// Calculate dynamic scaling for precise coordinate mapping
			const scaleX = containerWidth / config.badgeWidth;
			const scaleY = containerHeight / config.badgeHeight;

			// Update config with precise real coordinates (maintain float precision)
			const realX = newLeft / scaleX;
			const realY = newTop / scaleY;

			let newConfig = { ...config };

			if (draggedElement.id === 'qrElement') {
				newConfig.qrCode.x = Math.round(this.clamp(realX, 0, config.badgeWidth - config.qrCode.size));
				newConfig.qrCode.y = Math.round(this.clamp(realY, 0, config.badgeHeight - config.qrCode.size));
			} else if (draggedElement.id.startsWith('field-')) {
				const fieldIndex = parseInt(draggedElement.id.split('-')[1]);
				if (newConfig.fieldsConfig[fieldIndex]) {
					newConfig.fieldsConfig[fieldIndex].x = Math.round(this.clamp(realX, 0, config.badgeWidth - 5));
					newConfig.fieldsConfig[fieldIndex].y = Math.round(this.clamp(realY, 0, config.badgeHeight - 5));
				}
			}

			this.setState({ config: newConfig, dragUpdate: this.state.dragUpdate + 1, editing: true });
		}
	};

	handleMouseUp = () => {
		const { draggedElement } = this.state;
		if (draggedElement) {
			draggedElement.style.zIndex = '';
			this.setState({
				draggedElement: null,
				isResizing: false,
				resizeHandle: null,
				originalRect: null,
				originalConfig: null
			});
		}
	};

	handleInputChange = (field, value) => {
		const { config } = this.state;
		let newConfig = { ...config };

		if (field.startsWith('field-')) {
			const parts = field.split('-');
			const index = parseInt(parts[1]);
			const prop = parts[2];
			if (newConfig.fieldsConfig[index]) {
		if (prop === 'x' || prop === 'y') {
			newConfig.fieldsConfig[index][prop] = Math.round(parseFloat(value) || 0);
		} else if (prop === 'fontSize') {
			newConfig.fieldsConfig[index][prop] = parseInt(value) || 14;
		} else if (prop === 'fontFamily') {
			newConfig.fieldsConfig[index][prop] = value || 'Inter';
		}
			}
		} else if (field === 'qrSize') {
			newConfig.qrCode.size = Math.round(parseFloat(value) || 50);
		} else if (field === 'qrX') {
			newConfig.qrCode.x = Math.round(parseFloat(value) || 0);
		} else if (field === 'qrY') {
			newConfig.qrCode.y = Math.round(parseFloat(value) || 0);
		} else if (field === 'backgroundImage') {
			newConfig.backgroundImage = value;
		} else if (field === 'includeBackground') {
			newConfig.includeBackground = value;
		} else if (field === 'enableQrCode') {
			newConfig.enableQrCode = value;
		}

		this.setState({ config: newConfig, editing: true }, this.updateDisplay);
	};

	handleSave = async () => {
		await this.props.handleChange({ badgeConfiguration: this.state.config });
		await this.props.saveCheckInAppConfiguration();
		this.setState({ editing: false, baseConfiguration: JSON.parse(JSON.stringify(this.state.config)) });
	};

	cancelEdit = () => {
		this.setState({
			config: JSON.parse(JSON.stringify(this.state.baseConfiguration)),
			editing: false
		}, this.updateDisplay);
	};

	addFieldToBadge = (fieldValue) => {
		const { config, baseFields } = this.state;
		const fieldOption = baseFields.find(f => f.value === fieldValue.value);
		const newField = {
			field: fieldValue.value,
			label: fieldOption ? fieldOption.label : fieldValue.label,
			x: 10,
			y: 10,
			fontSize: 14,
			fontFamily: 'Inter'
		};
		const newConfig = { ...config };
		newConfig.fieldsConfig = [...newConfig.fieldsConfig, newField];
		this.setState({ config: newConfig, selectedBaseField: null, editing: true }, this.updateDisplay);
	};

	removeField = (index) => {
		const { config } = this.state;
		const newConfig = { ...config };
		newConfig.fieldsConfig.splice(index, 1);
		this.setState({ config: newConfig, editing: true }, this.updateDisplay);
	};



	handlePrint = () => {
		const { config } = this.state;

		// Use same canvas dimensions for identical appearance and precise positioning
		const canvasWidthPx = Math.round((88 / 25.4) * 96);   // Same as updateDisplay - actual 88mm at 96 PPI (rounded to whole pixels)
		const canvasHeightPx = Math.round((140 / 25.4) * 96); // Same as updateDisplay - actual 140mm at 96 PPI (rounded to whole pixels)

		// Calculate exact dimensions in inches for final print scaling
		const widthMm = 88;
		const heightMm = 140;
		const widthInches = widthMm / 25.4;
		const heightInches = heightMm / 25.4;

		// Create a new window for printing
		const printWindow = window.open('', 'PRINT', `width=${Math.round(canvasWidthPx)}px,height=${Math.round(canvasHeightPx)}px`);

		// Clone the badge container
		const badgeClone = this.badgeContainerRef.current.cloneNode(true);

		// Set badge dimensions to match canvas exactly (in pixels)
		badgeClone.style.width = canvasWidthPx + 'px';
		badgeClone.style.height = canvasHeightPx + 'px';
		badgeClone.style.border = 'none';
		badgeClone.style.position = 'relative';

		// Handle background image for print
		const backgroundImg = badgeClone.querySelector('img');
		if (backgroundImg) {
			// Reset all styles to match canvas exactly
			backgroundImg.style.cssText = '';
			backgroundImg.style.position = 'absolute';
			backgroundImg.style.top = '0';
			backgroundImg.style.left = '0';
			backgroundImg.style.width = '100%';
			backgroundImg.style.height = '100%';
			backgroundImg.style.objectFit = 'cover';
			backgroundImg.style.zIndex = '-1';
			backgroundImg.style.display = 'block';

			if (config.includeBackground) {
				backgroundImg.style.visibility = 'visible';
			} else {
				backgroundImg.style.display = 'none';
			}
		}

		// Use same scaling as canvas for identical positioning
		const scaleX = canvasWidthPx / config.badgeWidth; // Same as updateDisplay
		const scaleY = canvasHeightPx / config.badgeHeight; // Same as updateDisplay

		// Update element positions and sizes in pixels (same as canvas)
		config.fieldsConfig.forEach((field, index) => {
			const element = badgeClone.querySelector(`#field-${index}`);
			if (element) {
				element.style.cssText = '';
				element.className = '';
				element.style.position = 'absolute';
				element.style.margin = '0';
				element.style.padding = '0';
				element.style.left = (field.x * scaleX) + 'px';
				element.style.top = (field.y * scaleY) + 'px';
				element.style.fontSize = field.fontSize + 'px';
				element.style.fontFamily = field.fontFamily || 'Inter, sans-serif';
				element.style.color = '#000';

				// Remove resize handles
				const handles = element.querySelectorAll('.resize-handle');
				handles.forEach(handle => handle.remove());
			}
		});

		// Update QR code position
		const qrElement = badgeClone.querySelector('#qrElement');
		if (qrElement) {
			qrElement.style.cssText = '';
			qrElement.className = '';
			qrElement.style.position = 'absolute';
			qrElement.style.margin = '0';
			qrElement.style.padding = '0';
			qrElement.style.left = (config.qrCode.x * scaleX) + 'px';
			qrElement.style.top = (config.qrCode.y * scaleY) + 'px';
			qrElement.style.width = (config.qrCode.size * scaleX) + 'px';
			qrElement.style.height = (config.qrCode.size * scaleY) + 'px';
			qrElement.style.fontFamily = 'Inter, sans-serif';
			qrElement.style.backgroundColor = '#ccc';
			qrElement.style.border = '1px solid #999';
			qrElement.style.display = 'flex';
			qrElement.style.alignItems = 'center';
			qrElement.style.justifyContent = 'center';
			qrElement.style.fontSize = '12px';
			qrElement.style.color = '#666';

			// Remove resize handles
			const handles = qrElement.querySelectorAll('.resize-handle');
			handles.forEach(handle => handle.remove());
		}

		// Create print-specific HTML
		printWindow.document.write(`
			<!DOCTYPE html>
			<html>
			<head>
				<title>Badge Print</title>
				<style>
					* {
						margin: 0;
						padding: 0;
						box-sizing: border-box;
					}

					@font-face {
						font-family: 'Inter-Bold';
						src: url('https://system-fonts.s3.amazonaws.com/Inter/Inter-Bold.ttf') format('truetype');
						font-weight: bold;
						font-style: normal;
					}

					body, html {
						margin: 0;
						padding: 0;
						overflow: hidden;
					}

					.badge-container {
						width: ${Math.round(canvasWidthPx)}px;
						height: ${Math.round(canvasHeightPx)}px;
						page-break-inside: avoid !important;
						page-break-before: avoid !important;
						page-break-after: avoid !important;
					}

					@media print {
						@page {
							size: ${widthInches}in ${heightInches}in;
							margin: 0;
						}

						html, body {
							margin: 0 !important;
							padding: 0 !important;
							-webkit-print-color-adjust: exact !important;
							print-color-adjust: exact !important;
						}

						* {
							margin: 0 !important;
							padding: 0 !important;
							box-sizing: border-box !important;
						}

						img {
							display: block !important;
						}

						.badge-container {
							width: ${widthInches}in !important;
							height: ${heightInches}in !important;
							page-break-inside: avoid !important;
							page-break-before: avoid !important;
							page-break-after: avoid !important;
						}
					}
				</style>
			</head>
			<body>
		`);
		printWindow.document.write(badgeClone.outerHTML);
		printWindow.document.write(`
			</body>
			</html>
		`);

		printWindow.document.close();
		printWindow.focus();

		// Wait for content to load then print
		printWindow.onload = () => {
			// Short delay to ensure images are loaded
			setTimeout(() => {
				printWindow.print();
				setTimeout(() => {
					printWindow.close();
				}, 100);
			}, 500);
		};
	};

	toggleSection = (section) => {
		if (section === 'settings') {
			this.setState({ badgePanelOpen: !this.state.badgePanelOpen });
		}
	};

	render() {
		const { isOpen, toggle, eventId, orgId, links } = this.props;
		const { config, editing, baseFields } = this.state;

		return (
			<Modal isOpen={isOpen} toggle={toggle} className="fullscreen">
				<div className='modalHeader ph-20' style={{ borderBottom: '1px solid var(--background)' }}>
					<div className='headerContainer' style={{ maxWidth: 'unset', minHeight: 60, height: 60 }}>
						<div className='flex aic'>
							<div>
								<h2>Badge Builder</h2>
								<div className='flex aic '>
									<h4>Badge Editor</h4>
								</div>
							</div>
						</div>
						<div className='flex aic'>
							<button className="neu mr-25 " onClick={() => {
								if (editing) {
									if (window.confirm('Are you sure you want to discard your changes?')) {
										this.cancelEdit();
										this.props.toggle();
									}
								} else {
									this.props.toggle();
								}
							}
							}>{editing ? 'Close & Discard Changes' : 'Close'}</button>
							<button className={`neu  ${editing ? 'prim' : 'disabled'}`}
								onClick={() => {
									this.handleSave();
								}}>Save</button>
						</div>
					</div>
				</div>
				<ModalBody className="modalBody">
					<div className='flex jcsb w-100 h-100'>
						<div className='designerPreview'>
							<div className='pagePreviewContainer2'>
								<style>
									{`
										.badge-element:hover .resize-handle {
											opacity: 1 !important;
										}
										.resize-handle:hover {
											background-color: #000 !important;
											border-color: #fff !important;
										}
									`}
								</style>
								<div
									className="badge-container"
									ref={this.badgeContainerRef}
									onMouseDown={this.handleMouseDown}
									style={{
										width: `${Math.round((88 / 25.4) * 96)}px`,  // 333px - actual 88mm at 96 PPI (rounded to whole pixels)
										height: `${Math.round((140 / 25.4) * 96)}px`, // 529px - actual 140mm at 96 PPI (rounded to whole pixels)
										position: 'relative',
										backgroundColor: '#f9f9f9'
									}}
								>
									<img
										ref={this.backgroundImgRef}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: '100%',
											objectFit: 'cover',
											zIndex: -1,
											display: 'block',
											visibility: 'hidden'
										}}
									/>
									{config.fieldsConfig && config.fieldsConfig.map((field, index) => (
										<div
											key={index}
											ref={(el) => this.fieldRefs[`field-${index}`] = el}
											id={`field-${index}`}
											style={{
												position: 'absolute',
												cursor: 'move',
												color: '#000'
											}}
										>
											{field.label}<br />
											<div className="resize-handle nw" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'nw-resize',
												top: '-4px',
												left: '-4px'
											}}></div>
											<div className="resize-handle ne" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'ne-resize',
												top: '-4px',
												right: '-4px'
											}}></div>
											<div className="resize-handle sw" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'sw-resize',
												bottom: '-4px',
												left: '-4px'
											}}></div>
											<div className="resize-handle se" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'se-resize',
												bottom: '-4px',
												right: '-4px'
											}}></div>
										</div>
									))}
									{config.enableQrCode && (
										<div
											ref={this.qrElementRef}
											id="qrElement"
											style={{
												position: 'absolute',
												cursor: 'move',
												backgroundColor: '#ccc',
												border: '1px solid #999',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '12px',
												color: '#666'
											}}
										>
											QR Code<br />
											<div className="resize-handle nw" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'nw-resize',
												top: '-4px',
												left: '-4px'
											}}></div>
											<div className="resize-handle ne" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'ne-resize',
												top: '-4px',
												right: '-4px'
											}}></div>
											<div className="resize-handle sw" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'sw-resize',
												bottom: '-4px',
												left: '-4px'
											}}></div>
											<div className="resize-handle se" style={{
												position: 'absolute',
												width: '8px',
												height: '8px',
												backgroundColor: '#fff',
												border: '1px solid #000',
												opacity: 0,
												cursor: 'se-resize',
												bottom: '-4px',
												right: '-4px'
											}}></div>
										</div>
									)}
								</div>
							</div>
						</div>
						<div style={{ overflowX: 'hidden', position: 'relative' }} className={`designerPanel p-0 right ${this.state.badgePanelOpen ? '' : 'closed'}`}>
							<div style={{ overflowX: 'hidden', padding: 25, paddingBottom: 60, maxHeight: '100%' }} className={`formInputsSection selected`}>
								<button onClick={() => this.toggleSection('settings')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
								<h1 className='mb-15'>Badge Settings</h1>
								<div style={{ marginBottom: '15px' }}>
									<InputField
										label="Badge Width (px)"
										type="number"
										value={config.badgeWidth}
										disabled={true}
									/>
								</div>
								<div style={{ marginBottom: '15px' }}>
									<InputField
										label="Badge Height (px)"
										type="number"
										value={config.badgeHeight}
										disabled={true}
									/>
								</div>
								<div style={{ marginBottom: '15px' }}>
									<InputField
										label="Actual Size"
										type="text"
										value="88mm × 140mm (3.46in × 5.51in)"
										disabled={true}
									/>
								</div><div style={{ marginBottom: '15px' }}>
									<FileChooserPreview
										label="Background Image"
										value={config.backgroundImage}
										onClick={() => {
											this.setState({ uploadingBackground: true });
										}}
										clearFile={() => {
											this.handleInputChange('backgroundImage', '');
										}}
									/>
								</div>
								<div style={{ marginBottom: '15px' }}>
									<label style={{ display: 'block', marginBottom: '5px' }}>
										<input
											type="checkbox"
											checked={config.includeBackground}
											onChange={(e) => this.handleInputChange('includeBackground', e.target.checked)}
											style={{ marginRight: '5px' }}
										/>
										Include Background in Print
									</label>
								</div>

								<div>

									<ToggleField
										label="Enable QR Code on Badge"
										checked={config.enableQRCode}
										icons={false}
										onChange={(e) => {
											console.log(e.target.checked);
											this.handleInputChange('enableQRCode', e.target.checked);
										}}
									/>

								</div>


								{config.enableQRCode && (<>
									<div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
										<div style={{ flex: 1 }}>
											<InputField
												label="QR X Position"
												type="number"
												value={config.qrCode.x}
												onChange={(e) => this.handleInputChange('qrX', e.target.value)}
											/>
										</div>
										<div style={{ flex: 1 }}>
											<InputField
												label="QR Y Position"
												type="number"
												value={config.qrCode.y}
												onChange={(e) => this.handleInputChange('qrY', e.target.value)}
											/>
										</div>
									</div>
									<div style={{ marginBottom: '15px' }}>
										<InputField
											label="QR Size"
											type="number"
											value={config.qrCode.size}
											onChange={(e) => this.handleInputChange('qrSize', e.target.value)}
										/>
									</div>
								</>
								)}

								<div className='flex'>
									<SelectField
										classes="w-100"
										value={this.state.selectedBaseField}
										label="Add field to badge"
										options={baseFields}
										onChange={(e) => {
											this.setState({ selectedBaseField: e })
										}}
									/>
									<i className='las la-plus c-pointer ml-10 m-a' style={{ fontSize: 24 }} onClick={() => {
										if (this.state.selectedBaseField) {
											this.addFieldToBadge(this.state.selectedBaseField);
										}
									}}></i>
								</div>

								{config.fieldsConfig && config.fieldsConfig.map((field, index) => (
									<div key={index} style={{ marginBottom: '15px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
											<h4 style={{ margin: 0 }}>{field.label}</h4>
											<i className='las la-trash c-pointer' style={{ fontSize: 18, color: '#f00' }} onClick={() => this.removeField(index)}></i>
										</div>
										<div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
											<div style={{ flex: 1 }}>
												<InputField
													label="X Position"
													type="number"
													value={field.x}
													onChange={(e) => this.handleInputChange(`field-${index}-x`, e.target.value)}
												/>
											</div>
											<div style={{ flex: 1 }}>
												<InputField
													label="Y Position"
													type="number"
													value={field.y}
													onChange={(e) => this.handleInputChange(`field-${index}-y`, e.target.value)}
												/>
											</div>
										</div>
										<div style={{ marginBottom: '10px' }}>
											<InputField
												label="Font Size"
												type="number"
												value={field.fontSize}
												onChange={(e) => this.handleInputChange(`field-${index}-fontSize`, e.target.value)}
											/>
										</div>
										<div style={{ marginBottom: '10px' }}>
											<InputField
												label="Font Family"
												value={field.fontFamily}
												onChange={(e) => this.handleInputChange(`field-${index}-fontFamily`, e.target.value)}
											/>
										</div>
									</div>
								))}


								<div style={{ marginBottom: '10px' }}>
									<button onClick={this.handlePrint} style={{ width: '100%' }}>Print Badge</button>
								</div>
							</div>
						</div>
						<div className='designerPanel side p-0' style={{ overflow: 'hidden', position: 'relative', minWidth: 100, width: 100 }}>
							<div className='flex column jcc'>
								<div className={`flex column jcc aic c-pointer ${this.state.badgePanelOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
									this.toggleSection('settings')
								}}>
									<i className='las la-cog mb-10' style={{ fontSize: 30 }}></i>
									<h5>Settings</h5>
								</div>
							</div>
						</div>
					</div>
				</ModalBody>
				{this.state.uploadingBackground && (
					<FilesUploaderModal
						isOpen={this.state.uploadingBackground}
						toggle={() => {
							this.setState({ uploadingBackground: false });
						}}
						eventId={eventId}
						links={links}
						orgId={orgId}
						addFiles={(fileURL) => {
							this.handleInputChange('backgroundImage', fileURL);
							this.setState({ uploadingBackground: false });
						}}
					/>
				)}
			</Modal>
		);
	}
}

export default withRouter(BadgeEditor);
